export interface IDynamicFieldGroup {
  title?: string;
  fields: string[]; // Field names in this group
  columns?: 1 | 2 | 3 | 4;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

export interface IDynamicFormLayout {
  groups?: IDynamicFieldGroup[];
  defaultColumns?: 1 | 2 | 3 | 4;
}

// Enhanced DynamicFormWidget.tsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { DynamicField, DynamicFieldTypes, IDynamicCheckboxListField, IDynamicCurrencyField, IDynamicFileField, IDynamicFormState, IDynamicMultiSelectField, IDynamicNumberField, IDynamicSelectField, IDynamicTextAreaField, IDynamicTextField, IDynamiDatePickerField, prepareValuesForSubmission } from '.';
import GenericNumberInput from '../input-components/GenericNumberInput';
import GenericPhoneInput from '../input-components/GenericPhoneInput';
import GenericSelectInput from '../input-components/GenericSelectInput';
import { isEmpty, isValidNumber } from 'src/helpers/validation-utils';
import MainPageTitle from '../layout-components/main-layout/MainPageTitle';
import { Card, CircularProgress, Box, Grid, Accordion, AccordionSummary, AccordionDetails, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import GenericButton from '../GenericButton';
import { toast } from 'react-toastify';
import GenericTextAreaInput from '../input-components/GenericTextAreaInput';
import GenericFileInput from '../input-components/GenericFileInput';
import GenericDatePicker from '../input-components/GenericDatePicker';
import { formatDate } from 'src/helpers/formatting-utils';
import dayjs from 'dayjs';
import GenericInput from '../input-components/GenericTextInput';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface IDynamicFormWidgetProps {
  title: string;
  fields: Record<string, DynamicFieldTypes>;
  onSubmit: (values: Record<string, any>) => Promise<boolean>;
  drawerMode?: boolean;
  submitButtonText?: string;
  showCancelButton?: boolean;
  onCancel?: () => void;
  initialValues?: Record<string, any>;
  onFieldChange?: (name: string, value: any, allValues: Record<string, any>) => void;
  validationMode?: 'onBlur' | 'onChange' | 'onSubmit';
  className?: string;
  readOnly?: boolean;
  enableEnterSubmit?: boolean;
  autoFocusFirst?: boolean; // New prop for auto-focusing first field
  layout?: IDynamicFormLayout; // New prop for layout configuration
  children?: React.ReactNode; // New prop for custom children components
}

const useStyles = makeStyles()((_theme) => ({
  root: {
    padding: '20px 25px'
  },
  drawerRoot: {
    padding: '20px 0'
  },
  leftSection: {
    display: 'flex'
  },
  title: {
    display: 'block',
    marginRight: '15px'
  },
  searchField: {
    border: 'none',
    outline: 'none',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    fontSize: '16px',
    padding: '0 0 5px',
    margin: '0 30px 0 0'
  },
  fieldWrapper: {
    marginBottom: '16px',
    position: 'relative'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1
  },
  buttonGroup: {
    marginTop: '24px',
    display: 'flex',
    gap: '12px'
  },
  groupTitle: {
    marginBottom: '16px',
    marginTop: '8px',
    fontWeight: 600,
    fontSize: '1.1rem',
    color: '#333'
  },
  accordion: {
    marginBottom: '16px',
    '&:before': {
      display: 'none'
    },
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  accordionSummary: {
    backgroundColor: '#f5f5f5',
    '&.Mui-expanded': {
      minHeight: 48
    }
  },
  accordionDetails: {
    paddingTop: '16px'
  }
}));

const DynamicFormWidget: React.FC<IDynamicFormWidgetProps> = ({
  title,
  fields,
  onSubmit,
  drawerMode = false,
  submitButtonText = 'Submit',
  showCancelButton = false,
  onCancel,
  initialValues = {},
  onFieldChange,
  validationMode = 'onBlur',
  className,
  readOnly = false,
  enableEnterSubmit = false,
  autoFocusFirst = false,
  layout,
  children
}) => {
  const [pageState, setPageState] = useState<IDynamicFormState>();
  const { classes } = useStyles();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const firstFieldRef = useRef<any>(null);
  const fieldRefs = useRef<Record<string, any>>({});

  // Build dependency map
  const dependencyMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    Object.entries(fields).forEach(([fieldName, field]) => {
      if (field.dependsOn) {
        if (!map[field.dependsOn]) {
          map[field.dependsOn] = [];
        }
        map[field.dependsOn].push(fieldName);
      }
    });
    return map;
  }, [fields]);

  // Auto-focus first field
  useEffect(() => {
    if (autoFocusFirst && firstFieldRef.current && pageState) {
      setTimeout(() => {
        firstFieldRef.current?.focus();
      }, 100);
    }
  }, [autoFocusFirst, pageState]);

  useEffect(() => {
    const newPageState: IDynamicFormState = {
      values: {},
      errors: {},
      touched: {},
      loading: {},
      dependencies: dependencyMap
    };

    if (fields) {
      Object.values(fields).forEach((f) => {
        newPageState.values[f.name] = initialValues[f.name] ?? f.value;
        newPageState.errors[f.name] = '';
        newPageState.touched[f.name] = false;
        newPageState.loading[f.name] = false;
      });
      setPageState(newPageState);

      // Load initial async data for fields without dependencies
      Object.entries(fields).forEach(([fieldName, field]) => {
        if ((field.type === DynamicField.SELECT || field.type === DynamicField.MULTISELECT) && field.loadItems && !field.dependsOn) {
          loadFieldItems(fieldName, field);
        }
      });
    }
    // eslint-disable-next-line
  }, []);

  // Load async items for a field
  const loadFieldItems = useCallback(async (fieldName: string, field: DynamicFieldTypes, dependencyValue?: any) => {
    if (field.type !== DynamicField.SELECT && field.type !== DynamicField.MULTISELECT) return;
    if (!field.loadItems) return;

    setPageState(prev => ({
      ...prev!,
      loading: { ...prev!.loading, [fieldName]: true }
    }));

    try {
      const items = await field.loadItems(dependencyValue);

      // Update field items
      field.items = items;

      setPageState(prev => ({
        ...prev!,
        loading: { ...prev!.loading, [fieldName]: false }
      }));
    } catch (error) {
      console.error(`Error loading items for ${fieldName}:`, error);
      toast.error(`Failed to load ${field.title} options`);

      setPageState(prev => ({
        ...prev!,
        loading: { ...prev!.loading, [fieldName]: false }
      }));
    }
  }, []);

  const validateField = useCallback((fieldName: string, value: any, allValues: Record<string, any>): string => {
    const field = Object.values(fields).find((f) => f.name === fieldName);
    if (!field || field.hidden) return '';

    // Custom validation with all values
    if (field.customValidation) {
      const customError = field.customValidation(value, allValues);
      if (customError) return customError;
    }

    // Legacy custom validator
    if (field.customValidator) {
      const customError = field.customValidator(value);
      if (customError) return customError;
    }

    if (field.required && isEmpty(value)) {
      return 'Required';
    }

    if (isEmpty(value)) {
      return '';
    }

    switch (field.type) {
      case DynamicField.TEXT:
      case DynamicField.TEXTAREA: {
        const textField = field as IDynamicTextField | IDynamicTextAreaField;
        if (textField.minChars && value.length < textField.minChars) {
          return `Minimum ${textField.minChars} characters required`;
        }
        if (textField.maxChars && value.length > textField.maxChars) {
          return `Maximum ${textField.maxChars} characters allowed`;
        }
        if (textField.regex) {
          if (!textField.regex.test(value)) {
            return `Invalid ${field.title}`;
          }
        }
        return '';
      }

      case DynamicField.EMAIL: {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Invalid email address';
        }
        return '';
      }

      case DynamicField.CURRENCY:
      case DynamicField.NUMBER: {
        const numField = field as IDynamicNumberField | IDynamicCurrencyField;
        if (!isValidNumber(value)) {
          return 'Invalid Number!';
        }

        const fieldNumber = Number(value);

        if (numField.min !== undefined && fieldNumber < numField.min) {
          return 'Minimum value is ' + numField.min;
        }

        if (numField.max !== undefined && fieldNumber > numField.max) {
          return 'Maximum value is ' + numField.max;
        }
        return '';
      }

      case DynamicField.FILE: {
        const fileField = field as IDynamicFileField;
        if (value && typeof value !== 'string') {
          let fileValue = value as File;
          if (fileField.maxSizeInMbs && fileField.maxSizeInMbs > 0) {
            if (fileValue.size > fileField.maxSizeInMbs * 1024 * 1024) {
              return `Max file size is ${fileField.maxSizeInMbs} MB`;
            }
          }
        }
        return '';
      }

      case DynamicField.MULTISELECT:
      case DynamicField.CHECKBOXLIST: {
        const multiField = field as IDynamicMultiSelectField | IDynamicCheckboxListField;
        const arrayValue = value as string[];
        if (!Array.isArray(arrayValue)) {
          return 'Invalid';
        }

        if (field.required && arrayValue.length === 0) {
          return 'Required';
        }

        if (multiField.minOptions && arrayValue.length < multiField.minOptions) {
          return 'Minimum allowed options is ' + multiField.minOptions;
        }

        if (multiField.maxOptions && arrayValue.length > multiField.maxOptions) {
          return 'Maximum allowed options is ' + multiField.maxOptions;
        }
        return '';
      }

      case DynamicField.DATE: {
        const dateField = field as IDynamiDatePickerField;
        if (!dayjs(value).isValid()) {
          return 'Invalid Date!';
        }

        var fieldDate = dayjs(value);

        if (dateField.min) {
          if (fieldDate.isBefore(dateField.min)) {
            return 'Minimum Date is ' + formatDate(dateField.min);
          }
        }

        if (dateField.max) {
          if (fieldDate.isAfter(dateField.max)) {
            return 'Maximum Date is ' + formatDate(dateField.max);
          }
        }

        return '';
      }

    }
    return '';
  }, [fields]);

  const onValueChange = useCallback(async (name: string, value: any) => {
    if (!pageState) return;
    
    const field = fields[name];
    if (!field) return;

    let updatedValue = value;
    const newValues = { ...pageState.values, [name]: updatedValue };
    const newErrors = { ...pageState.errors };
    const newTouched = { ...pageState.touched };

    // Clear error if validation mode is onChange
    if (validationMode === 'onChange') {
      newErrors[name] = validateField(name, value, newValues);
      newTouched[name] = true;
    } else {
      newErrors[name] = '';
    }

    // Handle dependencies
    const dependentFields = pageState.dependencies[name] || [];
    for (const dependentFieldName of dependentFields) {
      const dependentField = fields[dependentFieldName];
      if (dependentField) {
        // Reset dependent field if configured
        if (dependentField.resetOnDependencyChange) {
          if (dependentField.type === DynamicField.MULTISELECT || dependentField.type === DynamicField.CHECKBOXLIST) {
            newValues[dependentFieldName] = [];
          } else if (dependentField.type === DynamicField.CHECKBOX) {
            newValues[dependentFieldName] = false;
          } else {
            newValues[dependentFieldName] = '';
          }
          newErrors[dependentFieldName] = '';
        }

        // Load new items for dependent field
        if ((dependentField.type === DynamicField.SELECT || dependentField.type === DynamicField.MULTISELECT) && dependentField.loadItems) {
          await loadFieldItems(dependentFieldName, dependentField, value);
        }
      }
    }

    const newPageState: IDynamicFormState = {
      ...pageState,
      values: newValues,
      errors: newErrors,
      touched: newTouched
    };
    setPageState(newPageState);

    // Call field's onChange handler
    if (field.onChange) {
      field.onChange(value, newValues);
    }

    // Call parent's field change handler
    if (onFieldChange) {
      onFieldChange(name, value, newValues);
    }
  }, [pageState, fields, validationMode, onFieldChange, validateField]);

const onFileValueChange = useCallback((name: string, value: any) => {
  if (!pageState) return;

  const field = fields[name];

  const newValues = { ...pageState.values, [name]: value };
  const newPageState: IDynamicFormState = {
    ...pageState,
    values: newValues,
    errors: { ...pageState.errors, [name]: '' },
    touched: { ...pageState.touched, [name]: true }
  };
  setPageState(newPageState);

  if (field?.onChange) {
    field.onChange(value, newValues);
  }
  if (onFieldChange) {
    onFieldChange(name, value, newValues);
  }
}, [pageState, fields, onFieldChange]);

  const onBlur = useCallback((name: string) => {
    if (!pageState || validationMode !== 'onBlur') return;

    const error = validateField(name, pageState.values[name], pageState.values);
    const newPageState: IDynamicFormState = {
      ...pageState,
      touched: { ...pageState.touched, [name]: true },
      errors: { ...pageState.errors, [name]: error }
    };
    setPageState(newPageState);
  }, [pageState, validationMode, validateField]);

  const validateForm = useCallback(() => {
    if (!pageState) return false;

    const newErrors: Record<string, string> = {};
    const newTouched: Record<string, boolean> = {};
    let isValid = true;

    Object.values(fields).forEach((f) => {
      if (!f.hidden) {
        const error = validateField(f.name, pageState.values[f.name], pageState.values);
        newErrors[f.name] = error;
        newTouched[f.name] = true;
        if (error) isValid = false;
      }
    });

    const newPageState = {
      ...pageState,
      errors: newErrors,
      touched: newTouched
    };

    setPageState(newPageState);
    return isValid;
  }, [pageState, fields, validateField]);

  const submit = useCallback(async () => {
    if (!submitting && pageState) {
      setSubmitting(true);
      const isValid = validateForm();

      if (isValid) {
        const dataToSubmit = prepareValuesForSubmission(pageState.values, fields);
        try {
          const success = await onSubmit(dataToSubmit);
          if (!success) {
            toast.error('Submission failed');
          }
        } catch (error) {
          console.error('Form submission error:', error);
          toast.error('An unexpected error occurred');
        }
        setSubmitting(false);
      } else {
        toast.error('Incomplete Form!');
        setSubmitting(false);
      }
    }
  }, [submitting, pageState, fields, onSubmit, validateForm]);

  const onKeyDown = useCallback((event: React.KeyboardEvent, fieldName: string) => {
    if (enableEnterSubmit && event.key === 'Enter' && !event.shiftKey) {
      // Don't submit on Enter for textarea fields
      const field = fields[fieldName];
      if (field?.type === DynamicField.TEXTAREA) {
        return;
      }

      event.preventDefault();

      // Get all visible fields
      const visibleFields = Object.values(fields).filter(f => !f.hidden);
      const currentFieldIndex = visibleFields.findIndex(f => f.name === fieldName);
      const isLastField = currentFieldIndex === visibleFields.length - 1;

      if (isLastField) {
        submit();
      } else {
        // Focus next field
        const nextField = visibleFields[currentFieldIndex + 1];
        if (nextField && fieldRefs.current[nextField.name]) {
          fieldRefs.current[nextField.name].focus();
        }
      }
    }
  }, [enableEnterSubmit, fields, submit]);

  useEffect(() => {
    if (!pageState) {
      setIsFormValid(false);
      return;
    }

    let isValid = true;
    for (const f of Object.values(fields)) {
      if (!f.hidden) {
        const error = validateField(f.name, pageState.values[f.name], pageState.values);
        if (error) {
          isValid = false;
          break;
        }
      }
    }
    setIsFormValid(isValid);
  }, [pageState?.values, fields, validateField]);

  const renderField = (field: DynamicFieldTypes, isFirstField: boolean = false) => {
    if (!pageState || field.hidden) return null;

    const commonProps = {
      name: field.name,
      title: field.title,
      value: pageState.values[field.name],
      onChange: (v: any) => onValueChange(field.name, v),
      onBlur: () => onBlur(field.name),
      onKeyDown: (event: React.KeyboardEvent) => onKeyDown(event, field.name),
      error: pageState.touched[field.name] ? pageState.errors[field.name] || '' : '',
      disabled: field.disabled || submitting || pageState.loading[field.name] || readOnly,
      placeholder: field.placeholder,
      helperText: field.helperText,
      inputRef: (ref: any) => {
        fieldRefs.current[field.name] = ref;
        if (isFirstField) {
          firstFieldRef.current = ref;
        }
      }
    };

    const fieldContent = (() => {
      switch (field.type) {
        case DynamicField.TEXT:
        case DynamicField.EMAIL:
          return (
            <GenericInput
              {...commonProps}
              autofocus={isFirstField}
              type={field.type === DynamicField.EMAIL ? 'email' : (field as IDynamicTextField).inputType || 'text'}
            />
          );

        case DynamicField.TEXTAREA:
          return (
            <GenericTextAreaInput
              {...commonProps}
              rows={(field as IDynamicTextAreaField).rows}
            />
          );

        case DynamicField.NUMBER:
          return (
            <GenericNumberInput
              {...commonProps}
            />
          );

        case DynamicField.PHONENUMBER:
        case DynamicField.CURRENCY:
          return (
            <GenericPhoneInput
              {...commonProps}
              type="text"
            />
          );

        case DynamicField.DATE:
          return (
            <GenericDatePicker
              {...commonProps}
              minDate={(field as IDynamiDatePickerField).min}
              maxDate={(field as IDynamiDatePickerField).max}
            />
          );

        case DynamicField.SELECT:
          return (
            <GenericSelectInput
              {...commonProps}
              type=""
              clearable={field.clearable}
              items={(field as IDynamicSelectField).items}
            />
          );

        case DynamicField.MULTISELECT:
          return (
            <GenericSelectInput
              {...commonProps}
              type=""
              multiple
              items={(field as IDynamicMultiSelectField).items}
            />
          );

        case DynamicField.FILE:
          return (
            <GenericFileInput
              {...commonProps}
              onChange={(v: any) => onFileValueChange(field.name, v)}
              allowedTypes={(field as IDynamicFileField).allowedTypes}
              multiple={(field as IDynamicFileField).multiple}
            />
          );

        case DynamicField.CHECKBOX:
          return (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!pageState.values[field.name]}
                  onChange={(e) => onValueChange(field.name, e.target.checked)}
                  disabled={field.disabled || submitting || readOnly}
                  name={field.name}
                />
              }
              label={field.title}
            />
          );

        default:
          return null;
      }
    })();

    return (
      <Box key={field.name} className={classes.fieldWrapper}>
        {fieldContent}
        {pageState.loading[field.name] && (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    );
  };

  const renderFieldsWithLayout = () => {
    const visibleFields = Object.values(fields).filter(f => !f.hidden);
    let isFirstField = true;

    if (layout?.groups) {
      // Render with groups
      return layout.groups.map((group, groupIndex) => {
        const groupFields = group.fields
          .map(fieldName => fields[fieldName])
          .filter(field => field && !field.hidden);

        if (groupFields.length === 0) return null;

        const columns = group.columns || layout.defaultColumns || 1;
        
        const content = (
          <Grid container spacing={2}>
            {groupFields.map((field, index) => {
              const isFirst = isFirstField;
              if (isFirstField) isFirstField = false;
              
              return (
                <Grid item xs={12} sm={columns === 1 ? 12 : 6} md={12 / columns} key={field.name}>
                  {renderField(field, isFirst)}
                </Grid>
              );
            })}
          </Grid>
        );

        if (group.collapsible) {
          return (
            <Accordion 
              key={groupIndex} 
              defaultExpanded={group.defaultExpanded !== false}
              className={classes.accordion}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                className={classes.accordionSummary}
              >
                <Typography>{group.title || `Section ${groupIndex + 1}`}</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                {content}
              </AccordionDetails>
            </Accordion>
          );
        }

        return (
          <Box key={groupIndex}>
            {group.title && (
              <Typography className={classes.groupTitle}>
                {group.title}
              </Typography>
            )}
            {content}
          </Box>
        );
      });
    } else {
      // Render without groups (with default columns if specified)
      const columns = layout?.defaultColumns || 1;
      
      if (columns === 1) {
        // Original behavior
        return visibleFields.map((field, index) => 
          renderField(field, index === 0)
        );
      }

      // Multi-column layout
      return (
        <Grid container spacing={2}>
          {visibleFields.map((field, index) => (
            <Grid item xs={12} sm={12 / columns} md={12 / columns} key={field.name}>
              {renderField(field, index === 0)}
            </Grid>
          ))}
        </Grid>
      );
    }
  };

  const renderBody = () => {
    return (
      <>
        {renderFieldsWithLayout()}
        {children && (
          <Box sx={{ mt: 2 }}>
            {children}
          </Box>
        )}
        <Box className={classes.buttonGroup}>
          <GenericButton
            type="button"
            onClick={submit}
            disabled={submitting || readOnly || !isFormValid}
            text={submitButtonText}
          />
          {showCancelButton && onCancel && (
            <GenericButton
              type="button"
              onClick={onCancel}
              disabled={submitting}
              text="Cancel"
              variant="outlined"
            />
          )}
        </Box>
      </>
    );
  };

  return pageState ? (
    <div className={className}>
      {title && <MainPageTitle title={title} />}
      {drawerMode ? (
        renderBody()
      ) : (
        <Card className={drawerMode ? classes.drawerRoot : classes.root}>
          {renderBody()}
        </Card>
      )}
    </div>
  ) : (
    <></>
  );
};

export default DynamicFormWidget;